import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import AuthCard from '@/components/AuthCard';
import {
  TrendingUp,
  TrendingDown,
  Radio,
  Bot,
  LineChart,
  ArrowRight,
  CheckCircle,
  UserPlus,
  Zap,
} from 'lucide-react';

export default function LandingPage({ onLogin, onForgotPassword, onShowAbout }) {
  const [authMode, setAuthMode] = useState('register');

  const scrollToSignup = (mode) => {
    if (mode) setAuthMode(mode);
    const el = document.getElementById('signup-card');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0c] text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0a0a0c]/80 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-lg">
                Trading Pilot <span className="text-blue-500">360</span>
              </span>
            </div>
            <div className="flex items-center space-x-3 sm:space-x-6">
              <button
                onClick={onShowAbout}
                className="text-gray-400 hover:text-white transition-colors text-sm font-medium hidden sm:block"
                data-testid="landing-nav-about"
              >
                About
              </button>
              <Button
                onClick={() => scrollToSignup('login')}
                variant="outline"
                className="border-white/20 hover:bg-white/10"
                data-testid="landing-header-login"
              >
                Login
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* HERO — 2-column (original premium copy + embedded signup) */}
      <section className="pt-28 sm:pt-32 pb-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        {/* Ambient gradient background — original styling */}
        <div className="absolute inset-0 bg-gradient-to-b from-blue-500/10 via-transparent to-transparent pointer-events-none" />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-[100px] pointer-events-none" />
        <div className="absolute top-1/3 right-1/4 w-64 h-64 bg-purple-500/10 rounded-full blur-[80px] pointer-events-none" />

        <div className="max-w-7xl mx-auto relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* LEFT — original hero copy */}
            <div className="text-center lg:text-left">
              {/* Badge */}
              <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-400 text-sm mb-6">
                <Zap className="w-4 h-4 mr-2" />
                AI-Powered Trading Automation
              </div>

              {/* Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-5 leading-tight">
                Turn TradingView alerts into automated trades —{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-600">
                  safely
                </span>
              </h1>

              {/* Subheadline */}
              <p className="text-lg sm:text-xl text-gray-400 mb-6 max-w-xl mx-auto lg:mx-0">
                Connect your alerts, let the system filter high-quality setups, and execute
                trades automatically in a risk-managed paper account.
              </p>

              {/* Trust bullets */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 mb-3 text-sm text-gray-400">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-blue-500" />
                  <span>No coding or bots to configure</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-blue-500" />
                  <span>Trades only high-probability setups</span>
                </div>
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-blue-500" />
                  <span>Fully automated risk management</span>
                </div>
              </div>

              {/* Future promise — progression from paper → real capital */}
              <p
                className="mt-3 mb-8 text-sm text-gray-400 flex items-center justify-center lg:justify-start gap-1.5"
                data-testid="hero-future-promise"
              >
                <ArrowRight className="w-3.5 h-3.5 text-gray-500" />
                Go live with real capital when you&apos;re ready
              </p>

              <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-3">
                <Button
                  onClick={() => scrollToSignup('register')}
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-7 h-12 text-base font-semibold"
                  data-testid="hero-start-free-btn"
                >
                  Start Free Trading
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <p className="text-xs text-gray-500">
                  No approval needed · Instant access · Risk-free paper trading
                </p>
              </div>
            </div>

            {/* RIGHT — signup card */}
            <div id="signup-card" className="flex flex-col items-center lg:items-end scroll-mt-20">
              <p
                className="text-sm font-semibold text-blue-400 mb-3 flex items-center gap-1.5"
                data-testid="signup-card-strap"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
                Start in 30 seconds — no setup required
              </p>
              <AuthCard
                onLogin={onLogin}
                onForgotPassword={onForgotPassword}
                mode={authMode}
                onModeChange={setAuthMode}
                variant="embedded"
              />
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="py-6 px-4 sm:px-6 lg:px-8 border-y border-white/5 bg-[#0d0d10]">
        <p
          className="text-center text-sm text-gray-400"
          data-testid="trust-strip"
        >
          <span className="text-gray-300 font-medium">No approval needed</span>
          <span className="mx-2 text-gray-600">·</span>
          <span className="text-gray-300 font-medium">Instant access</span>
          <span className="mx-2 text-gray-600">·</span>
          <span className="text-gray-300 font-medium">
            Fully simulated — no real money at risk
          </span>
        </p>
      </section>

      {/* PRODUCT PREVIEW */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">
              See your performance{' '}
              <span className="text-blue-500">in real time</span>
            </h2>
            <p className="text-gray-400 text-base sm:text-lg">
              Every trade is tracked, analyzed, and ready for review.
            </p>
          </div>

          {/* Stylized dashboard mock — built with the same components as the live app */}
          <DashboardPreview />
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-[#0d0d10]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold">How it works</h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <Step
              icon={<UserPlus className="w-6 h-6" />}
              num="01"
              title="Sign up"
              desc="Create your free account in under 30 seconds. No approval, no payment."
            />
            <Step
              icon={<Bot className="w-6 h-6" />}
              num="02"
              title="Let the system scan & trade"
              desc="Live signals are evaluated and executed automatically in your paper account."
            />
            <Step
              icon={<LineChart className="w-6 h-6" />}
              num="03"
              title="Review your performance"
              desc="Equity curve, win rate, daily P&L, and reflection notes — all in one place."
            />
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">
            Ready to see what fully automated trading feels like?
          </h2>
          <p className="text-gray-400 mb-8 text-base sm:text-lg">
            Join traders running their strategies on autopilot — risk-free.
          </p>
          <Button
            onClick={() => scrollToSignup('register')}
            size="lg"
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 h-12 text-base font-semibold"
            data-testid="final-cta-start-free"
          >
            Start Free
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <p className="mt-4 text-xs text-gray-500">
            Takes less than 30 seconds · No approval required
          </p>
        </div>
      </section>

      {/* Footer — minimal, centered, breathable */}
      <footer className="mt-24 sm:mt-32 py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto flex flex-col items-center text-center">
          {/* Logo */}
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-white">
              Trading Pilot <span className="text-blue-500">360</span>
            </span>
          </div>

          {/* Links */}
          <nav className="mb-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-sm text-gray-400">
            <a href="/about" className="hover:text-white transition-colors" data-testid="footer-about-link">About</a>
            <span aria-hidden className="text-gray-700">·</span>
            <a href="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy Policy</a>
            <span aria-hidden className="text-gray-700">·</span>
            <a href="/terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms of Service</a>
            <span aria-hidden className="text-gray-700">·</span>
            <a href="/disclaimer" className="hover:text-white transition-colors" data-testid="footer-disclaimer-link">Disclaimer</a>
            <span aria-hidden className="text-gray-700">·</span>
            <a href="/refund-policy" className="hover:text-white transition-colors" data-testid="footer-refund-link">Refund Policy</a>
          </nav>

          {/* Legal disclaimer */}
          <p className="mb-2 text-xs text-gray-500" data-testid="footer-disclaimer-strap">
            Not financial advice. Trading involves risk.
          </p>

          {/* Copyright */}
          <p className="text-[11px] text-gray-600">
            © {new Date().getFullYear()} Trading Pilot 360. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

function Step({ icon, num, title, desc }) {
  return (
    <Card className="bg-[#13131a] border-white/5 p-6 hover:border-blue-500/30 transition-colors">
      <div className="flex items-center justify-between mb-4">
        <div className="w-11 h-11 bg-blue-500/10 rounded-lg flex items-center justify-center text-blue-500">
          {icon}
        </div>
        <span className="text-xs font-bold text-gray-600 tracking-wider">{num}</span>
      </div>
      <h3 className="text-lg font-semibold mb-2 text-white">{title}</h3>
      <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
    </Card>
  );
}

/**
 * Stylized in-product preview that matches the live Paper Trading dashboard
 * (hero balance card + status strip + mini equity curve + recent-trade chips).
 * Built with HTML/CSS so we don't need to host a real screenshot asset.
 */
function DashboardPreview() {
  return (
    <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-[#0d0d10] via-[#111114] to-[#0d0d10] p-2 sm:p-3 shadow-[0_30px_60px_-30px_rgba(59,130,246,0.35)]">
      {/* Window chrome */}
      <div className="flex items-center gap-1.5 px-3 py-2">
        <span className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
        <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
        <span className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
        <span className="ml-3 text-[11px] text-gray-500 font-mono">
          tradingpilot360.app/paper-trading
        </span>
      </div>

      <div className="rounded-xl bg-[#0a0a0c] border border-white/5 p-4 sm:p-6 space-y-5">
        {/* Hero balance */}
        <div className="rounded-xl border border-white/5 bg-gradient-to-br from-blue-500/10 via-[#111] to-[#111] p-4 sm:p-5">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
            <span className="text-sm font-semibold text-gray-400">Account Balance:</span>
            <span className="text-3xl sm:text-4xl font-black font-mono text-blue-400 leading-none">
              $1,247.82
            </span>
            <span className="text-base sm:text-lg font-mono font-bold text-green-400">
              +$247.82
              <span className="text-xs text-gray-400 ml-1.5 font-semibold">(Total Profit)</span>
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-3">
            Risk 1.0% = <span className="text-gray-300 font-mono">$12.48</span> per trade
          </p>
        </div>

        {/* Status + Today */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-lg border border-white/5 bg-[#111] px-4 py-3 flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            <span className="text-sm text-green-400 font-medium">
              System actively scanning markets…
            </span>
          </div>
          <div className="rounded-lg border border-green-500/30 bg-green-500/5 px-4 py-3 flex items-center justify-between">
            <span className="text-xs uppercase tracking-wider font-bold text-gray-400">Today</span>
            <span className="text-lg font-black font-mono text-green-400">+$42.10</span>
            <span className="text-xs text-gray-500">3 trades · 66% win</span>
          </div>
        </div>

        {/* Mini equity curve */}
        <div className="rounded-lg border border-white/5 bg-[#111] p-4">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs uppercase tracking-wider font-bold text-gray-400">
              Equity Curve · Last 14 days
            </span>
            <span className="text-xs font-mono text-green-400">+24.78%</span>
          </div>
          <svg viewBox="0 0 400 80" className="w-full h-20" preserveAspectRatio="none">
            <defs>
              <linearGradient id="eq-grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#10B981" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path
              d="M0,60 L30,55 L60,58 L90,48 L120,52 L150,40 L180,45 L210,32 L240,36 L270,24 L300,28 L330,18 L360,22 L400,12"
              fill="none"
              stroke="#10B981"
              strokeWidth="2"
            />
            <path
              d="M0,60 L30,55 L60,58 L90,48 L120,52 L150,40 L180,45 L210,32 L240,36 L270,24 L300,28 L330,18 L360,22 L400,12 L400,80 L0,80 Z"
              fill="url(#eq-grad)"
            />
          </svg>
        </div>

        {/* Recent trades */}
        <div className="space-y-1.5">
          <span className="text-xs uppercase tracking-wider font-bold text-gray-400 block mb-2">
            Recent trades
          </span>
          <TradeRow symbol="EURUSD" dir="long" pnl={18.4} status="TP" />
          <TradeRow symbol="NAS100" dir="long" pnl={42.1} status="TP" />
          <TradeRow symbol="GBPUSD" dir="short" pnl={-12.3} status="SL" />
        </div>
      </div>
    </div>
  );
}

function TradeRow({ symbol, dir, pnl, status }) {
  const positive = pnl >= 0;
  return (
    <div className="flex items-center justify-between rounded-md bg-[#0d0d10] border border-white/5 px-3 py-2 text-sm">
      <div className="flex items-center gap-2">
        <Radio className="w-3.5 h-3.5 text-blue-500" />
        <span className="font-mono font-semibold text-gray-200">{symbol}</span>
        <span
          className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
            dir === 'long' ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'
          }`}
        >
          {dir}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <span
          className={`text-[10px] font-bold tracking-wider px-1.5 py-0.5 rounded ${
            status === 'TP' ? 'bg-green-500/15 text-green-400' : 'bg-red-500/15 text-red-400'
          }`}
        >
          {status} HIT
        </span>
        <span
          className={`font-mono font-semibold ${
            positive ? 'text-green-400' : 'text-red-400'
          }`}
        >
          {positive ? <TrendingUp className="w-3.5 h-3.5 inline -mt-0.5 mr-1" /> : <TrendingDown className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />}
          {positive ? '+' : '−'}${Math.abs(pnl).toFixed(2)}
        </span>
      </div>
    </div>
  );
}
